"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

interface InterestSubmission {
    id: string;
    user_first_name: string;
    user_last_name: string;
    user_email: string;
    target_community: string;
    user_home_community: string | null;
    selected_options: string[];
    status: string;
    submitted_at: string;
    program_journey?: string | null;
    membership_journey?: string | null;
}

const ROLE_MAPPING: Record<string, string> = {
    'charterholder': 'Charterholder',
    'charterholderinactive': 'Charterholder inactive',
    'level3candidate': 'Candidate L3',
    'level2candidate': 'Candidate L2',
    'level1candidate': 'Candidate L1',
    'l3eligible': 'Level 3 Eligible',
    'l2eligible': 'Level 2 Eligible',
    'l1eligible': 'Level 1 Eligible',
    'professional_member': 'Professional member',
    'affiliate_member': 'Affiliate Member',
    'professional_member_inactive': 'Professional member inactive',
    'affiliate_member_inactive': 'Affiliate Member inactive',
    'Never a Candidate': 'Never a Candidate',
    'Never a Member': 'Never a Member',
    'l3_cleared': 'L3 Cleared'
};

const formatJourneyRole = (val: string) => {
    return ROLE_MAPPING[val] || val;
};

export function InterestSubmissionsView() {
    const [submissions, setSubmissions] = useState<InterestSubmission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/v1/interest/submissions`);

                if (res.status === 403) {
                    setError("You do not have permission to view this data.");
                    return;
                }

                if (!res.ok) {
                    throw new Error("Failed to fetch submissions");
                }

                const data = await res.json();
                setSubmissions(data);
            } catch (err: any) {
                console.error(err);
                setError("An error occurred while fetching submissions.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchSubmissions();
    }, []);

    if (isLoading) {
        return <div className="p-8 text-center text-gray-500">Loading interest submissions...</div>;
    }

    if (error) {
        return (
            <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                <p className="text-red-600 font-medium">Debug Error: {error}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h3 className="text-xl font-bold text-[#003d45] mb-1">Interest Submissions</h3>
                    <p className="text-sm text-gray-500">
                        Members who have expressed interest in joining communities or chapters.
                    </p>
                </div>
                <Link
                    href="/staff?section=volunteers_interested"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#007a8a] hover:text-[#003d45] px-3.5 py-2 rounded-xl border border-gray-200 hover:border-[#007a8a] bg-gray-50/50 hover:bg-white transition-all shadow-2xs w-fit"
                >
                    <span>Open in Volunteers Interested tab</span>
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </Link>
            </div>

            <div className="p-6">
                {submissions.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No submissions found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-3 font-semibold">Date</th>
                                    <th className="px-6 py-3 font-semibold">Member</th>
                                    <th className="px-6 py-3 font-semibold">Target Group</th>
                                    <th className="px-6 py-3 font-semibold">Home Community</th>
                                    <th className="px-6 py-3 font-semibold">Interests</th>
                                </tr>
                            </thead>
                            <tbody>
                                {submissions.map((sub) => (
                                    <tr key={sub.id} className="bg-white border-b hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                            {new Date(sub.submitted_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{sub.user_first_name} {sub.user_last_name}</div>
                                            <div className="text-xs text-gray-500">{sub.user_email}</div>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {sub.program_journey && sub.program_journey !== "Never a Candidate" && (
                                                    <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-medium border border-blue-100">
                                                        {formatJourneyRole(sub.program_journey)}
                                                    </span>
                                                )}
                                                {sub.membership_journey && sub.membership_journey !== "Never a Member" && (
                                                    <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[10px] font-medium border border-purple-100">
                                                        {formatJourneyRole(sub.membership_journey)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-700">{sub.target_community}</td>
                                        <td className="px-6 py-4">
                                            {sub.user_home_community ? (
                                                sub.user_home_community === sub.target_community ? (
                                                    <span className="text-green-600 text-xs font-medium">Matches target</span>
                                                ) : (
                                                    <span className="text-gray-700">{sub.user_home_community}</span>
                                                )
                                            ) : (
                                                <span className="text-gray-400 text-xs">Unknown</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {sub.selected_options.map((opt, idx) => (
                                                    <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[10px] font-medium border border-gray-200">
                                                        {opt}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
